import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { 
  MessageSquare, Search, Filter, MoreVertical, 
  Archive, Eye, Reply, Trash2
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useMessages } from '@/hooks/useMessages';
import { useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const MessagesPage = () => {
  const navigate = useNavigate();
  const messages = useMessages();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<string>('all');

  const filteredMessages = messages.data.filter(message => {
    const matchesSearch = message.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         message.snippet?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterType === 'all' || 
                         (filterType === 'unread' && !message.read_at) ||
                         (filterType === 'system' && message.from_system) ||
                         (filterType === 'user' && !message.from_system);
    return matchesSearch && matchesFilter;
  });

  const handleMessageClick = (message: any) => {
    // Mark as read if not already read
    if (!message.read_at) {
      messages.markAsRead([message.id]);
    }
    
    // Navigate to message detail (placeholder for now)
    navigate(`/inbox/messages/${message.id}`);
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <MessageSquare className="h-8 w-8 text-accent" />
          <div>
            <h1 className="text-3xl font-bold text-text-primary">Messages</h1>
            <p className="text-text-secondary">Internal messages and communications</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <Badge className="bg-chip-bg text-chip-text">
            {messages.unreadCount} unread
          </Badge>
          {messages.unreadCount > 0 && (
            <Button
              variant="outline"
              onClick={() => messages.markAsRead(messages.data.filter(m => !m.read_at).map(m => m.id))}
              disabled={messages.isMarkingRead}
            >
              <Eye className="h-4 w-4 mr-2" />
              Mark All Read
            </Button>
          )}
        </div>
      </div>

      {/* Search and Filter Bar */}
      <Card className="bg-bg-elevated border-border-subtle">
        <CardContent className="p-4">
          <div className="flex items-center space-x-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-text-muted" />
              <Input
                placeholder="Search messages..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-bg-subtle border-border-subtle focus:border-brand"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Filter className="h-4 w-4 text-text-muted" />
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-3 py-2 bg-bg-subtle border border-border-subtle rounded-md text-text-primary focus:border-brand focus:outline-none"
              >
                <option value="all">All Messages</option>
                <option value="unread">Unread Only</option>
                <option value="system">System Messages</option>
                <option value="user">User Messages</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Messages List */}
      <Card className="bg-bg-elevated border-border-subtle">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>All Messages ({filteredMessages.length})</span>
            {filteredMessages.length > 0 && (
              <span className="text-sm font-normal text-text-secondary">
                Showing {filteredMessages.length} of {messages.data.length} messages
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <ScrollArea className="max-h-[600px]">
            <div className="divide-y divide-border-subtle">
              {filteredMessages.map((message, index) => (
                <div
                  key={message.id}
                  className={`p-4 hover:bg-hover-bg transition-colors cursor-pointer ${
                    !message.read_at ? 'bg-accent/5 border-l-2 border-l-accent' : ''
                  }`}
                  onClick={() => handleMessageClick(message)}
                >
                  <div className="flex items-start justify-between space-x-4">
                    <div className="flex items-start space-x-3 flex-1">
                      <MessageSquare className="h-5 w-5 text-accent mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          <h3 className={`text-sm font-medium ${
                            !message.read_at ? 'text-text-primary' : 'text-text-secondary'
                          }`}>
                            {message.subject}
                          </h3>
                          {message.from_system && (
                            <Badge variant="outline" className="text-xs">
                              System
                            </Badge>
                          )}
                          {!message.read_at && (
                            <div className="w-2 h-2 bg-accent rounded-full" />
                          )}
                        </div>
                        
                        {message.snippet && (
                          <p className="text-sm text-text-muted mb-2 line-clamp-2">
                            {message.snippet}
                          </p>
                        )}
                        
                        <div className="flex items-center space-x-4 text-xs text-text-muted">
                          <span>
                            {formatDistanceToNow(new Date(message.created_at), { addSuffix: true })}
                          </span>
                          {message.from_user_id && (
                            <span>From: User</span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="p-1 h-auto">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {!message.read_at && (
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              messages.markAsRead([message.id]);
                            }}
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            Mark as Read
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            // TODO: Implement reply functionality
                          }}
                        >
                          <Reply className="h-4 w-4 mr-2" />
                          Reply
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            messages.archiveMessage(message.id);
                          }}
                        >
                          <Archive className="h-4 w-4 mr-2" />
                          Archive
                        </DropdownMenuItem>
                        <Separator />
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            // TODO: Implement delete functionality
                          }}
                          className="text-danger focus:text-danger"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
              
              {filteredMessages.length === 0 && (
                <div className="text-center py-12">
                  <MessageSquare className="h-12 w-12 mx-auto text-text-muted opacity-50 mb-4" />
                  <h3 className="text-lg font-medium text-text-primary mb-2">
                    {messages.data.length === 0 ? 'No messages yet' : 'No matching messages'}
                  </h3>
                  <p className="text-text-muted">
                    {messages.data.length === 0 
                      ? 'You\'ll see important messages and communications here.'
                      : 'Try adjusting your search or filter criteria.'
                    }
                  </p>
                </div>
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
};

export default MessagesPage;